import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ConstantsService} from '../../fidelidade/ConstantsService';
import {EstadoOnboardingPainel, OnboardingPainelService, PASSOS_ONBOARDING, PassoOnboarding, PassoOnboardingId}
  from '../onboarding-painel.service';
import {OnbAjudaComponent} from '../apoio/onb-ajuda.component';
import {OnbFocoService} from '../apoio/onb-foco.service';

/** Ícone e tempo de cada passo; o ícone colorido serve para reconhecer o passo antes de ler. */
const VISUAL: Record<PassoOnboardingId, {icone: string; tempo: string}> = {
  cardapio: {icone: 'fas fa-book-open', tempo: '~4 min'},
  loja: {icone: 'fas fa-store', tempo: '~2 min'},
  pagamento: {icone: 'far fa-credit-card', tempo: '~1 min'},
  receber: {icone: 'fas fa-bell', tempo: '~1 min'},
  teste: {icone: 'fas fa-shopping-bag', tempo: '~2 min'}
};

/**
 * Primeiros passos (/admin/comecar e /admin/comecar/:passo): o hub com os cinco passos da loja criada pela Mia Gestor
 * e, dentro dele, cada passo compacto. Ao salvar um passo, segue para o próximo que falta; no fim, a tela Pronto.
 */
@Component({
  selector: 'app-comecar',
  templateUrl: './comecar.component.html',
  styleUrls: ['./comecar.component.scss']
})
export class ComecarComponent implements OnInit, OnDestroy {
  @ViewChild(OnbAjudaComponent) ajudaPainel: OnbAjudaComponent;

  estado: EstadoOnboardingPainel | null = null;
  empresa: any = {};
  passo: string | null = null;
  carregando = true;
  erro = '';

  private destruir$ = new Subject<void>();

  constructor(private onboarding: OnboardingPainelService, private constants: ConstantsService, private router: Router,
              private route: ActivatedRoute, private foco: OnbFocoService) {}

  ngOnInit(): void {
    this.constants.empresa$.pipe(takeUntil(this.destruir$)).subscribe(empresa => this.empresa = empresa || {});
    this.route.paramMap.pipe(takeUntil(this.destruir$)).subscribe(params => {
      const passo = params.get('passo');
      this.passo = passo && ([...PASSOS_ONBOARDING, 'pronto'] as string[]).includes(passo) ? passo : null;
      this.foco.feche();
      window.scrollTo(0, 0);
      if (this.passo && this.passo !== 'pronto') this.onboarding.registre('PASSO_ABERTO', this.passo);
      this.carregue();
    });
  }

  ngOnDestroy(): void {
    this.foco.feche();
    this.destruir$.next();
    this.destruir$.complete();
  }

  carregue(): void {
    this.carregando = !this.estado;
    this.erro = '';
    this.onboarding.estado().then(estado => this.estado = estado)
      .catch(() => this.erro = 'Não foi possível carregar os primeiros passos.')
      .finally(() => this.carregando = false);
  }

  get faltam(): number { return (this.estado?.total || 0) - (this.estado?.feitos || 0); }

  get linkCurto(): string { return String(this.empresa?.linkLoja || '').replace(/^https?:\/\//, '').replace(/\/$/, ''); }

  get proximo(): PassoOnboarding | null { return this.estado?.passos?.find(p => p.id === this.estado?.proximo) || null; }

  /** Fatos da ficha da Mia que valem mostrar, na ordem em que o dono lê. */
  get fatosDaMia(): Array<{rotulo: string; valor: string}> {
    const m = this.estado?.mia;
    if (!m) return [];
    return [
      {rotulo: 'Loja', valor: this.empresa?.nome || m.restaurante},
      {rotulo: 'Segmento', valor: m.segmento},
      {rotulo: 'Atende', valor: m.operacao},
      {rotulo: 'Vende por', valor: m.canais},
      {rotulo: 'Usa hoje', valor: m.ferramentaAtual},
      {rotulo: 'Instagram', valor: m.instagram},
      {rotulo: 'Cidade', valor: m.cidade}
    ].filter(f => !!f.valor);
  }

  icone(p: PassoOnboarding): string { return p.feito ? 'fas fa-check' : VISUAL[p.id].icone; }

  tempo(p: PassoOnboarding): string { return VISUAL[p.id].tempo; }

  ehProximo(p: PassoOnboarding): boolean { return !p.feito && this.estado?.proximo === p.id; }

  abra(p: PassoOnboarding | PassoOnboardingId): void {
    const id = typeof p === 'string' ? p : p.id;
    this.router.navigate(['/admin/comecar', id]);
  }

  /** Continuar do hub: sempre habilitado, leva ao próximo passo que falta. */
  continue(): void {
    if (this.estado?.proximo) return this.abra(this.estado.proximo);
    this.router.navigate(['/admin/comecar', 'teste']);
  }

  /** Passo salvo: atualiza o estado e segue para o próximo que falta (ou volta ao hub). */
  aoConcluirPasso(estado?: EstadoOnboardingPainel): void {
    const anterior = this.passo;
    const seguir = (novo: EstadoOnboardingPainel) => {
      this.estado = novo;
      if (anterior) this.onboarding.registre('PASSO_FEITO', anterior);
      if (anterior === 'receber') this.constants.recarregueEmpresa();
      const proximo = novo?.proximo;
      if (proximo && proximo !== anterior) this.router.navigate(['/admin/comecar', proximo]);
      else this.router.navigate(['/admin/comecar']);
    };
    if (estado) seguir(estado);
    else this.onboarding.estado().then(seguir).catch(() => this.router.navigate(['/admin/comecar']));
  }

  aoConcluirTeste(estado: EstadoOnboardingPainel): void {
    this.estado = estado;
    this.onboarding.registre('PASSO_FEITO', 'teste');
    // o menu passa para a fase Pronta e o Início deixa de redirecionar para cá
    this.constants.recarregueEmpresa();
    this.router.navigate(['/admin/comecar', 'pronto']);
  }

  atualize(estado: EstadoOnboardingPainel): void { this.estado = estado; }

  pecaAjudaDe(evento: {motivo: string; passo: string}): void { this.ajudaPainel?.abra(evento.motivo, evento.passo); }
}
